import axios, { AxiosResponse } from 'axios';
import {
  User,
  ApiTokenSummary,
  CreateTokenRequest,
  CreateTokenResponse,
  RotateTokenResponse,
  UsageStats,
  BillingInfo,
  Invoice,
  Coupon,
  LoginRequest,
  RegisterRequest,
  CreateCouponRequest,
  ProxyRequest,
  ProxyResponse,
  PaystackPlan,
  PaystackInitResponse,
  Currency,
  UsageSummary,
  AccountBalance,
  PurchaseCreditsRequest,
  PurchaseCreditsResponse,
  PaymentHistoryResponse,
  RedeemCouponResponse,
  MyCouponsResponse,
  NotificationsResponse,
} from '@/types';

const API_BASE_URL = import.meta.env.MODE === 'development'
  ? import.meta.env.VITE_API_BASE_URL || 'https://enterprise-api-gateway.findout.workers.dev'
  : import.meta.env.VITE_API_BASE_URL || 'https://enterprise-api-gateway.findout.workers.dev';

// Create axios instance with default config
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  login: (data: LoginRequest): Promise<AxiosResponse<{ user: User; token: string }>> =>
    apiClient.post('/auth/login', data),

  register: (data: RegisterRequest): Promise<AxiosResponse<{ user: User; token: string }>> =>
    apiClient.post('/auth/register', data),

  logout: (): Promise<AxiosResponse<void>> =>
    apiClient.post('/auth/logout'),

  me: (): Promise<AxiosResponse<User>> =>
    apiClient.get('/auth/me'),

  refreshToken: (): Promise<AxiosResponse<{ token: string }>> =>
    apiClient.post('/auth/refresh'),
};

// Tokens API
export const tokensApi = {
  list: () => apiClient.get<ApiTokenSummary[]>('/auth/tokens'),

  create: (data: CreateTokenRequest) =>
    apiClient.post<CreateTokenResponse>('/auth/token', data),

  revoke: (id: string) => apiClient.delete<{ status: string }>(`/auth/token/${id}`),

  rotate: (id: string) => apiClient.post<RotateTokenResponse>(`/auth/token/${id}/rotate`),
};

// Usage API
export const usageApi = {
  getStats: (period?: string): Promise<AxiosResponse<UsageStats>> =>
    apiClient.get('/usage/stats', { params: { period } }),

  getTokenUsage: (tokenId: string, period?: string): Promise<AxiosResponse<UsageStats>> =>
    apiClient.get(`/usage/tokens/${tokenId}`, { params: { period } }),

  getSummary: (params?: { start?: number; end?: number; granularity?: 'hour' | 'day' | 'month'; environment?: 'sandbox' | 'production' }) =>
    apiClient.get<UsageSummary>('/usage', { params }),
};

// Billing API
export const billingApi = {
  getInfo: (): Promise<AxiosResponse<BillingInfo>> =>
    apiClient.get('/billing'),

  getInvoices: (): Promise<AxiosResponse<Invoice[]>> =>
    apiClient.get('/billing/invoices'),

  downloadInvoice: (invoiceId: string): Promise<AxiosResponse<Blob>> =>
    apiClient.get(`/billing/invoices/${invoiceId}/download`, { responseType: 'blob' }),

  updatePaymentMethod: (paymentMethodId: string): Promise<AxiosResponse<BillingInfo>> =>
    apiClient.patch('/billing/payment-method', { paymentMethodId }),

  cancelSubscription: (): Promise<AxiosResponse<void>> =>
    apiClient.post('/billing/cancel'),

  getBalance: () => apiClient.get<AccountBalance>('/billing/balance'),
  setLowBalanceThreshold: (threshold: number) =>
    apiClient.put<{ low_balance_threshold: number }>('/billing/low-balance', { threshold }),

  purchaseCredits: (data: PurchaseCreditsRequest) =>
    apiClient.post<PurchaseCreditsResponse>('/billing/prepaid', data),

  getHistory: (page: number = 1, limit: number = 20) =>
    apiClient.get<PaymentHistoryResponse>('/billing/history', { params: { page, limit } }),
};

// Paystack API
export const paystackApi = {
  initializeTransaction: (data: {
    email: string;
    amount: number;
    currency: Currency;
    plan?: string;
    reference?: string;
    callbackUrl?: string;
  }): Promise<AxiosResponse<PaystackInitResponse>> =>
    apiClient.post('/billing/paystack/initialize', data),

  verifyTransaction: (reference: string): Promise<AxiosResponse<{ status: boolean; data: any }>> =>
    apiClient.post('/billing/paystack/verify', { reference }),

  getPlans: (currency?: Currency): Promise<AxiosResponse<PaystackPlan[]>> =>
    apiClient.get('/billing/plans', { params: { currency } }),

  subscribe: (data: {
    plan: string;
    currency: Currency;
  }): Promise<AxiosResponse<{ subscription: any }>> =>
    apiClient.post('/billing/subscribe', data),

  cancelSubscription: (): Promise<AxiosResponse<void>> =>
    apiClient.post('/billing/cancel'),

  switchCurrency: (currency: Currency): Promise<AxiosResponse<{ currency: Currency }>> =>
    apiClient.patch('/billing/currency', { currency }),
};

// Coupons API (admin). The gateway's coupon routes and fields differ from the
// dashboard's Coupon shape, so they are translated here:
//   list GET /admin/coupons · create POST /coupon/create
//   switch on/off PUT /admin/coupon/{id} · delete DELETE /admin/coupon/{id}
// "fixed" coupons give credits at once (gateway type fixed_credits); "percentage"
// coupons add that share of extra credits to the redeemer's next purchase.
interface GatewayCoupon {
  id: string;
  code: string;
  description?: string | null;
  type: 'percentage' | 'fixed_credits';
  value: number;
  max_uses: number;
  used_count: number;
  expires_at?: number | null;
  is_active: boolean | number;
}

const fromGateway = (c: GatewayCoupon): Coupon => ({
  id: c.id,
  code: c.code,
  description: c.description ?? '',
  discountType: c.type === 'fixed_credits' ? 'fixed' : 'percentage',
  discountValue: c.value,
  isActive: !!c.is_active,
  expiresAt: c.expires_at ? new Date(c.expires_at * 1000).toISOString() : undefined,
  usageCount: c.used_count ?? 0,
  usageLimit: c.max_uses,
});

/** A yyyy-mm-dd expiry date means the end of that day, Lagos time. */
const expiryToUnix = (date: string): number => Math.floor(new Date(`${date}T23:59:59+01:00`).getTime() / 1000);

export const couponsApi = {
  getAll: async (): Promise<AxiosResponse<Coupon[]>> => {
    const res = await apiClient.get<{ coupons: GatewayCoupon[] }>('/admin/coupons', { params: { limit: 100 } });
    return { ...res, data: (res.data.coupons ?? []).map(fromGateway) };
  },

  create: async (data: CreateCouponRequest): Promise<AxiosResponse<Coupon>> => {
    const res = await apiClient.post<GatewayCoupon>('/coupon/create', {
      code: data.code,
      description: data.description || undefined,
      type: data.discountType === 'fixed' ? 'fixed_credits' : 'percentage',
      value: data.discountValue,
      max_uses: data.usageLimit,
      expires_at: data.expiresAt ? expiryToUnix(data.expiresAt) : undefined,
    });
    return { ...res, data: fromGateway(res.data) };
  },

  delete: (id: string): Promise<AxiosResponse<void>> =>
    apiClient.delete(`/admin/coupon/${encodeURIComponent(id)}`),

  setActive: (id: string, isActive: boolean): Promise<AxiosResponse<{ message: string }>> =>
    apiClient.put(`/admin/coupon/${encodeURIComponent(id)}`, { is_active: isActive }),
};

// Platform credits an admin gives a user directly (gateway billing/creditGrants.ts).
export interface CreditGrant {
  id: string;
  user_id: string;
  email?: string | null;
  full_name?: string | null;
  credits: number;
  reason: string;
  granted_by_email?: string | null;
  created_at: number;
  balance?: number;
}

export const creditGrantsApi = {
  grant: (userId: string, credits: number, reason: string) =>
    apiClient.post<CreditGrant>(`/admin/users/${encodeURIComponent(userId)}/credits`, { credits, reason }),

  list: (userId?: string) =>
    apiClient.get<{ grants: CreditGrant[] }>('/admin/credit-grants', { params: userId ? { user_id: userId } : {} }),
};

// Coupon Redemption API (real, user-facing — distinct from couponsApi's admin-shaped CRUD above)
export const redemptionApi = {
  redeem: (code: string) =>
    apiClient.post<RedeemCouponResponse>('/coupon/redeem', { code }),

  getMy: () => apiClient.get<MyCouponsResponse>('/coupon/my'),
};

// Notifications API
export const notificationsApi = {
  list: (before?: number) =>
    apiClient.get<NotificationsResponse>('/notifications', { params: before ? { before } : {} }),

  getUnreadCount: () => apiClient.get<{ count: number }>('/notifications/unread-count'),

  markRead: (id: string) => apiClient.post<{ status: string }>(`/notifications/${id}/read`),

  markAllRead: () => apiClient.post<{ status: string; count: number }>('/notifications/read-all'),
};

// FX API — a public, keyless, CORS-enabled rate source. Deliberately bypasses
// apiClient: different host, no auth header wanted, no gateway involvement.
export const fxApi = {
  getNgnPerUsd: async (): Promise<number> => {
    const response = await fetch('https://open.er-api.com/v6/latest/USD');
    const data = await response.json();
    return data.rates.NGN;
  },
};

// Account / Settings API
export interface ProfileDetails {
  email: string;
  full_name: string;
  company?: string | null;
  phone?: string | null;
  marketing_consent: boolean;
  data_processing_consent: boolean;
  consent_updated_at?: number | null;
  notification_prefs: Record<string, boolean> | null;
  ip_whitelist: string[];
}

export interface UpdateProfileRequest {
  full_name?: string;
  company?: string;
  phone?: string;
  marketing_consent?: boolean;
  data_processing_consent?: boolean;
  notification_prefs?: Record<string, boolean>;
  ip_whitelist?: string[];
}

export const accountApi = {
  getProfile: () => apiClient.get<ProfileDetails>('/me'),

  updateProfile: (data: UpdateProfileRequest) => apiClient.patch<ProfileDetails>('/me', data),

  changePassword: (current_password: string, new_password: string) =>
    apiClient.post<{ status: string }>('/auth/password', { current_password, new_password }),

  exportData: () => apiClient.get('/privacy/export', { responseType: 'blob' }),

  deleteAccount: (password: string) =>
    apiClient.post<{ status: string }>('/privacy/delete', { password }),
};

// Proxy API
export const proxyApi = {
  makeRequest: (data: ProxyRequest): Promise<AxiosResponse<ProxyResponse>> =>
    apiClient.post('/proxy', data),
};

// Utility functions for API error handling
export const getErrorMessage = (error: any): string => {
  if (error.response?.data?.error) {
    return error.response.data.error;
  }
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  if (error.message) {
    return error.message;
  }
  return 'An unexpected error occurred';
};

export const isApiError = (error: any): boolean => {
  return error.response && error.response.status >= 400;
};

// Trip history from Latlens Maps (opt-in; gateway maps/trips.ts)
export interface TripSettings { available: boolean; country: string; enabled: boolean; retention_months: number }
export interface RankedPlace { name: string; lon: number; lat: number; visits: number; first_visit: number; last_visit: number; score: number }
export interface TripSummary {
  settings: TripSettings;
  totals: { distance_m: number; duration_s: number; trips: number; arrivals: number; places: number; since: number | null };
  places: RankedPlace[];
  by_month: { month: string; distance_m: number; trips: number }[];
  recent: { ended_at: number; mode: string; distance_m: number; duration_s: number; arrived: boolean; name: string }[];
}

export const tripsApi = {
  summary: () => apiClient.get<TripSummary>('/me/trips/summary'),
  updateSettings: (change: Partial<Pick<TripSettings, 'enabled' | 'retention_months'>>) => apiClient.put<TripSettings>('/me/trips/settings', change),
  deleteAll: () => apiClient.delete<{ deleted: number }>('/me/trips'),
};

// Per-country privacy gates for personal-data features (super admins; gateway maps/privacyProfiles.ts)
export interface PrivacyProfile {
  country_code: string; law: string; regulator: string;
  gates_required: string[]; gates_met: string[]; features_enabled: string[];
  approved_by: string | null; approved_at: number | null; updated_at: number;
}

export const privacyProfilesApi = {
  list: () => apiClient.get<{ profiles: PrivacyProfile[]; features: string[] }>('/admin/privacy-profiles'),
  update: (country: string, gates_met: string[], features_enabled: string[]) =>
    apiClient.put<PrivacyProfile>(`/admin/privacy-profiles/${encodeURIComponent(country)}`, { gates_met, features_enabled }),
};
